import { resolve } from "./resolve";
import axios from "axios";
import { API_AUTH_URL, USER_ROLES } from "../constants/consts";
import i18n from "i18next";

export function getSession() {
  return localStorage.getItem("sessionId");
}

export function setSession(sessionId) {
  const result = localStorage.setItem("sessionId", sessionId);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("sessionIdChanged"));
  }
  return result;
}

export function clearSession() {
  localStorage.removeItem("sessionId");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("sessionIdChanged"));
  }
}

export function setSessionUserRole(role) {
  return localStorage.setItem("userRole", role);
}

export function getSessionUserRole() {
  return localStorage.getItem("userRole");
}

export function setUserEmail(email) {
  return localStorage.setItem("userEmail", email);
}

export function getUserEmail() {
  return localStorage.getItem("userEmail");
}

export function clearSessionUserRole() {
  return localStorage.removeItem("userRole");
}

export function clearLocalStorage() {
  const userId = getUserId();
  if (userId) {
    clearUserLanguage(userId);
  }
  cachedUserId = null;

  const tempLang = getTempLanguage();
  clearTempLanguage();

  localStorage.clear();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("sessionIdChanged"));
  }

  if (!tempLang) {
    const browserLang = navigator.language || "en-GB";
    i18n.changeLanguage(browserLang);
  }
}

export function clearLocalStorageForRegistration() {
  const userId = getUserId();
  if (userId) {
    clearUserLanguage(userId);
  }
  cachedUserId = null;

  const tempLang = getTempLanguage();
  localStorage.clear();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("sessionIdChanged"));
  }

  if (tempLang) {
    setTempLanguage(tempLang);
  }
}

export function setSessionUserCompany(companyName) {
  return localStorage.setItem("companyName", companyName);
}

export function getSessionUserCompany() {
  return localStorage.getItem("companyName");
}

export function setIsUserLoggedIn(isUserLoggedIn) {
  return localStorage.setItem("isUserLoggedIn", isUserLoggedIn);
}

export function getIsUserLoggedIn() {
  return localStorage.getItem("isUserLoggedIn");
}

export function clearIsUserLoggedIn() {
  return localStorage.removeItem("isUserLoggedIn");
}

export async function changeUserRoleForQuestionaryBlock() {
  return await resolve(
    axios
      .patch(`${API_AUTH_URL}/users/role`, {
        email: getUserEmail(),
        role: USER_ROLES.WAITING,
      })
      .then((res) => res)
  );
}

export async function createNewRole() {
  return await resolve(
    axios
      .post(`${API_AUTH_URL}/roles`, {
        roles: [USER_ROLES.WAITING],
      })
      .then((res) => res)
  );
}

export async function checkUserRole() {
  return await resolve(
    axios
      .post(`${API_AUTH_URL}/users/check`, {
        session_id: getSession(),
      })
      .then((res) => res)
  );
}

export function setIsTwoFactorAuthorized(isTwoFactorAuthorized) {
  return localStorage.setItem("isTwoFactorAuthorized", isTwoFactorAuthorized);
}

export function getIsTwoFactorAuthorized() {
  return JSON.parse(localStorage.getItem("isTwoFactorAuthorized"));
}

export function getTwoFactorEnabled() {
  return JSON.parse(localStorage.getItem("isEnabledTwoFactor"));
}

export function setTwoFactorEnabled(isEnabledTwoFactor) {
  return localStorage.setItem("isEnabledTwoFactor", isEnabledTwoFactor);
}

let cachedUserId = null;

export function getUserId() {
  if (cachedUserId === null) {
    const stored = localStorage.getItem("userId");
    cachedUserId = stored ? JSON.parse(stored) : null;
  }
  return cachedUserId;
}

export function setUserId(userId) {
  cachedUserId = userId;
  return localStorage.setItem("userId", userId);
}

export function setUserLanguage(userId, language) {
  return localStorage.setItem(`user_${userId}_language`, language);
}

export function getUserLanguage(userId) {
  return localStorage.getItem(`user_${userId}_language`);
}

export function clearUserLanguage(userId) {
  return localStorage.removeItem(`user_${userId}_language`);
}

export function setTempLanguage(language) {
  return localStorage.setItem("temp_registration_language", language);
}

export function getTempLanguage() {
  return localStorage.getItem("temp_registration_language");
}

export function clearTempLanguage() {
  return localStorage.removeItem("temp_registration_language");
}

/**
 * @param {string} text
 * @param {string | null} [endDate]
 */
export function setPreAuthBlockedPage(text, endDate = null) {
  localStorage.setItem("preAuthBlockedPage", JSON.stringify({ text, endDate }));
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("sessionIdChanged"));
  }
}

export function getPreAuthBlockedPage() {
  const raw = localStorage.getItem("preAuthBlockedPage");
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function clearPreAuthBlockedPage() {
  localStorage.removeItem("preAuthBlockedPage");
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("sessionIdChanged"));
  }
}

export async function changePasswordBySession(password_old, password_new) {
  const sessionId = getSession();
  if (!sessionId) throw new Error("No session ID");
  const { data, error } = await resolve(
    axios.post(
      `${API_AUTH_URL}/users/change/password`,
      {
        session_id: sessionId,
        password_old,
        password_new,
      },
      {
        headers: {
          session_id: sessionId,
        },
      }
    )
  );

  if (error) {
    const status = error.response?.status;
    const message = error.response?.data?.detail || "Password change failed";

    const err = { status, message };
    throw err;
  }

  return data.data || data;
}
